import { Contact, ContactStatus, LocationType, OrganizationType, StudentType } from '@wonderschool/common-base-types';
import { fetchEmailUsage } from '../../../api/firebase/users';
import { createContactForStudent, uninviteContact, updateContactForStudent } from '../../../contacts/contactsAPI';
import { determineContactStatus } from '../../../contacts/determineContactStatus';
import { formatFullName, phoneNumberFormat } from '../../../helpers/utils';
import { logError } from '../../../rollbar';
import { RESPONSIBLE_FOR_BILLING_TYPE } from '../../consts';
import { saveStudent } from '../../studentsAPI';
import { FamilyModalFormData } from './types';

type SuccessReturnType = {
  isSuccess: true;
  successToastData: {
    contactDisplayName: string;
  };
};

type ErrorReturnType = {
  isSuccess: false;
  failedToUninvite?: true;
  errorSavingParent?: {
    contactDisplayName: string;
  };
  noStudentOrOrganization?: true;
  emailAlreadyExists?: true;
  // note: shouldStopSaving should be investigating if it is needed or can we get rid of it and say
  // "all isSuccess cases mean that we should stop saving"
  shouldStopSaving?: true;
  existingContact?: Contact;
};

interface TransformAndSaveEditedContactProps {
  formData: FamilyModalFormData;
  initialContact?: Contact;
  allOrganizationContacts: Contact[];
  organization: OrganizationType;
  location: LocationType;
  student: StudentType;
  isAdd: boolean;
  isMobileOrgSwitcherEnabled?: boolean;
}

export async function transformAndSaveEditedContact({
  formData,
  initialContact,
  allOrganizationContacts,
  organization,
  location,
  student,
  isAdd,
  isMobileOrgSwitcherEnabled = false,
}: TransformAndSaveEditedContactProps): Promise<SuccessReturnType | ErrorReturnType> {
  const { sendInvitation, isResponsibleForBilling, allowParentLogin, allowStaffLogin, ...rest } = formData;

  if (!student?.id || !organization?.id) {
    return {
      isSuccess: false,
      shouldStopSaving: true,
    };
  }

  rest.phone = phoneNumberFormat(rest.phone);
  rest.displayName = formatFullName(rest, true);

  const email = formData.email?.toLowerCase();
  const originalEmail = initialContact?.email?.toLowerCase();

  // note: we run into this case when we add a new contact, this is confusing and should be fixed
  const isEditEmail = originalEmail !== email;

  // If email is being changed and there's an existing invitation, uninvite first
  if (isEditEmail && initialContact?.invitationId && initialContact?.status !== ContactStatus.verified) {
    try {
      // TODO: this saves the contact again! I don't think this should happen here! I think there should be a separate
      // modal here warning the user that they are changing the email and that it will uninvite the contact.
      // Or this is entirely wrong and we need to have a decent email change form that handles this the right way.
      await uninviteContact(initialContact);
      // Update status to reflect uninvite
      rest.status = ContactStatus.new;
      rest.invitationId = null;
    } catch (error) {
      logError('Error uninviting contact', error);

      return {
        isSuccess: false,
        failedToUninvite: true,
        shouldStopSaving: true,
      };
    }
  }

  let hasNewUid = false;

  if ((isAdd || isEditEmail || sendInvitation) && !!email) {
    const matchingContact = allOrganizationContacts.find((_contact) => _contact?.email?.toLowerCase() === email);
    if (matchingContact && (isAdd || isEditEmail)) {
      return {
        isSuccess: false,
        existingContact: matchingContact,
      };
    }

    const emailUsage = await fetchEmailUsage(email, organization.id);

    // If flag is disabled, maintain "user already exists" restriction
    if (!isMobileOrgSwitcherEnabled && emailUsage.hasRootUser) {
      return {
        isSuccess: false,
        emailAlreadyExists: true,
      };
    }

    const result = determineContactStatus({
      sendInvitation,
      emailUsage: {
        hasAuthUser: emailUsage.hasAuthUser,
        uid: emailUsage.uid,
        existingContacts:
          emailUsage.existingContacts?.map((contact) => ({
            id: contact.id,
            status: contact.status,
            invitationId: contact.invitationId,
            uid: contact.uid,
          })) ?? [],
      },
      currentContact: {
        id: rest.id,
        status: rest.status as ContactStatus,
        invitationId: rest.invitationId ?? null,
      },
    });

    rest.status = result?.status ?? rest.status;

    if (result && 'invitationId' in result) {
      rest.invitationId = result.invitationId;
    } else {
      rest.invitationId = null;
    }

    if (result && 'uid' in result) {
      rest.uid = result.uid;
      hasNewUid = true;
    }
  }

  // The old uid stays on the contact; clearing verifiedAt keeps that old account switched off.
  if (isEditEmail && rest.uid && !hasNewUid) rest.verifiedAt = null;

  try {
    const { contactDocRef, contact } = isAdd
      ? await createContactForStudent(organization, location, student, rest)
      : await updateContactForStudent(organization, location, student, rest);

    // Add the contact to the student.family map so the UI is responsive.
    // The Student firestore listener will overwrite this with the correct data. DO NOT PERIST THIS HERE.
    // TODO: test if this can be removed as "student.family" is not used later in this code
    if (!student.family) student.family = {};
    if (!student.family[contactDocRef.id]) student.family[contactDocRef.id] = contact;

    if (rest.relationship && isResponsibleForBilling) {
      const responsibleForBillingType =
        rest.status === ContactStatus.verified
          ? RESPONSIBLE_FOR_BILLING_TYPE.parent
          : RESPONSIBLE_FOR_BILLING_TYPE.invitation;

      const data = {
        id: student.id,
        responsibleForBilling: {
          id: contactDocRef.id,
          displayName: rest.displayName,
          type: responsibleForBillingType,
          email: rest.email?.trim().toLowerCase(),
          modifiedDate: Date.now(),
          contactStatus: rest.status,
          uid: rest.uid ?? null,
        },
        uid: '',
        invitationId: '',
      };
      if (rest.uid) data.uid = rest.uid;
      if (rest.invitationId) data.invitationId = rest.invitationId;
      await saveStudent(organization.id, data);
    }

    return {
      isSuccess: true,
      successToastData: {
        contactDisplayName: rest.displayName,
      },
    };
  } catch (error) {
    console.log('Error updating parent', error);

    return {
      isSuccess: false,
      errorSavingParent: { contactDisplayName: rest.displayName },
    };
  }
}
